/**
 * Organization Detail Page
 * Shows organization details with Users and Tenants tabs.
 * Accessible to global_admin and organization_owner (own org only).
 */

import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useOrganization } from '../hooks/useOrganization';
import { useOrganizationTenants } from '../hooks/useOrganizationTenants';
import { usePendingRequests } from '../hooks/usePendingRequests';
import {
  Avatar,
  Button,
  Spinner,
  Text,
  Badge,
  makeStyles,
  tokens,
  shorthands,
  TableCellLayout,
  TableColumnDefinition,
  createTableColumn,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
} from '@fluentui/react-components';
import {
  ArrowClockwise20Regular,
  People20Regular,
  Organization20Regular,
  Add20Regular,
  Delete20Regular,
  Delete24Regular,
  Edit20Regular,
  Mail20Regular,
  ArrowSync20Regular,
} from '@fluentui/react-icons';
import { OrganizationApiService } from '../services/organizationApi';
import { Alert } from '../components/common/Alert';
import {
  PageLayout,
  DataGridCard,
  DataGridToolbar,
  ToolbarButton,
} from '../components/layouts';
import { useAuth } from '../contexts/AuthContext';
import { useTenantContext } from '../contexts/TenantContext';
import { isGlobalAdmin, Permission, hasPermission } from '../types/auth';
import type {
  OrganizationTenant,
  OrganizationStatus,
} from '../types/organization';
import { UserManagementPage, type UserManagementPageRef } from './UserManagementPage';
import { TenantRegistrationForm } from '../components/TenantManagement/TenantRegistrationForm';
import { TenantUserAccessModal } from '../components/Tenants/TenantUserAccessModal';
import { TenantStatusChangeModal } from '../components/Tenants/TenantStatusChangeModal';
import { createTextColumn } from '../components/common/DataGrid/factories/createTextColumn';
import { createColumnWithRowActions, type ActionConfig } from '../components/layouts/factories/columns';
import type { TenantRequest } from '../types/tenantRequest';
import { OrgUserCreateModal } from '../components/Organizations/OrgUserCreateModal';
import { AddTenantModal } from '../components/Organizations/AddTenantModal';
import { useQuery } from '@tanstack/react-query';
import type { OrganizationUser, OrganizationMember } from '../types/organization';

type DetailTab = 'users' | 'tenants' | 'requests';

const useStyles = makeStyles({
  headerInfo: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalL),
  },
  statusBadge: {
    textTransform: 'capitalize',
  },
  countBadge: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalXS),
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    ...shorthands.gap(tokens.spacingVerticalM),
  },
});

/**
 * Get status badge color
 */
const getStatusColor = (status: OrganizationStatus) => {
  switch (status) {
    case 'active':
      return 'success' as const;
    case 'suspended':
      return 'warning' as const;
    default:
      return 'informative' as const;
  }
};

/**
 * Get tenant status badge color
 */
const getTenantStatusColor = (status: string) => {
  switch (status) {
    case 'home':
      return 'brand' as const;
    case 'managed':
      return 'success' as const;
    case 'paused':
      return 'warning' as const;
    case 'archived':
      return 'danger' as const;
    default:
      return 'informative' as const;
  }
};

/**
 * Organization Detail Page Component
 */
export const OrganizationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const styles = useStyles();
  const { user } = useAuth();
  const { tenantList } = useTenantContext();

  // Find the home tenant for home_tenant orgs (users are created in home tenant)
  const homeTenant = tenantList.find((t) => t.status === 'home');

  // Determine if we should show the back button
  // Hide it for organization_owner viewing their own organization
  // (they can't access the organizations list anyway)
  const orgIdNumber = id ? parseInt(id, 10) : null;
  const isViewingOwnOrg = user?.organization_id === orgIdNumber;
  const showBackButton = isGlobalAdmin(user) || !isViewingOwnOrg;

  // Permission checks for action visibility
  const canManageAllTenants = hasPermission(user, Permission.MANAGE_ALL_TENANTS);
  const canManageTenantAccess = hasPermission(user, Permission.MANAGE_TENANT_ACCESS);
  // Org owners can request status changes but not directly manage tenant status
  const isOrgOwner = user?.role === 'organization_owner';

  // React Query hooks for data fetching
  const {
    organization,
    isLoading,
    error: organizationError,
    refetch: refetchOrganization,
  } = useOrganization(orgIdNumber ?? undefined);

  // Check if this is a federated organization (users managed in external Azure AD)
  const isFederatedOrg = organization?.authentication_mode === 'federated';

  // Fetch org members for federated organizations (users + pending invitations)
  const {
    data: orgMembers,
    refetch: refetchOrgMembers,
    isFetching: isFetchingOrgMembers,
  } = useQuery({
    queryKey: ['organizationMembers', orgIdNumber],
    queryFn: () => OrganizationApiService.listOrganizationMembers(orgIdNumber!),
    enabled: isFederatedOrg && orgIdNumber !== null,
  });

  const {
    tenants,
    isLoading: isLoadingTenants,
    isFetching: isFetchingTenants,
    removeTenant,
    isRemoving,
    refetch: refetchTenants,
  } = useOrganizationTenants(orgIdNumber ?? undefined);

  const {
    pendingRequests,
    isLoading: pendingRequestsLoading,
    isFetching: isFetchingPendingRequests,
    refetch: refetchPendingRequests,
  } = usePendingRequests(orgIdNumber ?? undefined);

  // Derive error from hook
  const error = organizationError?.message ?? null;

  // Check if tab is specified in URL (direct nav from CONFIGURE menu)
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as DetailTab | null;
  // Direct access to users tab shows simplified view (no tabs)
  // Direct access to tenants tab shows tenants + pending requests tabs
  const isDirectUsersAccess = tabFromUrl === 'users';
  const isDirectTenantsAccess = tabFromUrl === 'tenants';
  const [selectedTab, setSelectedTab] = useState<DetailTab>(tabFromUrl || 'users');

  // Search state for embedded UserManagementPage
  const [userSearchQuery, setUserSearchQuery] = useState('');
  // Ref to call UserManagementPage methods
  const userManagementRef = useRef<UserManagementPageRef>(null);

  // Dialog states
  const [isAddTenantDialogOpen, setIsAddTenantDialogOpen] = useState(false);
  const [unassignedTenants, setUnassignedTenants] = useState<OrganizationTenant[]>([]);
  // Org user create/edit modal state
  const [isOrgUserCreateModalOpen, setIsOrgUserCreateModalOpen] = useState(false);
  const [editingOrgUser, setEditingOrgUser] = useState<import('../types/organization').OrganizationUser | undefined>(undefined);
  // Pagination state for org users
  const [orgUsersPage, setOrgUsersPage] = useState(1);
  const orgUsersPageSize = 20;
  // Pagination state for tenants
  const [tenantsPage, setTenantsPage] = useState(1);
  const tenantsPageSize = 20;
  // Pagination state for pending requests
  const [requestsPage, setRequestsPage] = useState(1);
  const requestsPageSize = 20;
  // Selection state for org users (for bulk delete)
  const [selectedFederatedUsers, setSelectedFederatedUsers] = useState<Set<number>>(new Set());
  const [showFederatedBulkDeleteDialog, setShowFederatedBulkDeleteDialog] = useState(false);
  const [isFederatedBulkDeleting, setIsFederatedBulkDeleting] = useState(false);
  const [federatedBulkDeleteError, setFederatedBulkDeleteError] = useState<string | null>(null);
  // State for individual user actions
  const [resendingUserId, setResendingUserId] = useState<number | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);

  // Tenant request UI states (data comes from hook)
  const [isRequestTenantDialogOpen, setIsRequestTenantDialogOpen] = useState(false);

  // Tenant user access modal state
  const [isUserAccessModalOpen, setIsUserAccessModalOpen] = useState(false);
  const [userAccessTenant, setUserAccessTenant] = useState<OrganizationTenant | null>(null);

  // Tenant status change modal state (for org_owner to request status changes)
  const [isStatusChangeModalOpen, setIsStatusChangeModalOpen] = useState(false);
  const [statusChangeTenant, setStatusChangeTenant] = useState<OrganizationTenant | null>(null);

  // Mutation error state for UI display
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deletingUserName, setDeletingUserName] = useState<string>('');

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Auto-dismiss mutation error after 5 seconds
  useEffect(() => {
    if (mutationError) {
      const timer = setTimeout(() => setMutationError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [mutationError]);

  // Note: Data fetching is now managed by React Query hooks above
  // Users tab data is managed by embedded UserManagementPage

  /**
   * Handle refresh button
   * Note: Users tab refresh is handled by UserManagementPage's own refresh
   */
  const handleRefresh = () => {
    refetchOrganization();
    if (selectedTab === 'tenants') {
      refetchTenants();
      refetchPendingRequests();
    }
  };

  /**
   * Handle request tenant form success
   */
  const handleRequestTenantSuccess = () => {
    setIsRequestTenantDialogOpen(false);
    refetchPendingRequests();
  };

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    navigate('/app/organizations');
  };

  /**
   * Load unassigned tenants for the dialog
   */
  const loadUnassignedTenants = async () => {
    try {
      const availableTenants = await OrganizationApiService.listUnassignedTenants();
      setUnassignedTenants(availableTenants);
    } catch (err) {
      setMutationError(err instanceof Error ? err.message : 'Failed to load unassigned tenants');
    }
  };

  /**
   * Open add tenant dialog
   */
  const handleOpenAddTenantDialog = async () => {
    await loadUnassignedTenants();
    setIsAddTenantDialogOpen(true);
  };

  /**
   * Handle opening the user access modal for a tenant
   */
  const handleManageUserAccess = (tenant: OrganizationTenant) => {
    setUserAccessTenant(tenant);
    setIsUserAccessModalOpen(true);
  };

  /**
   * Handle remove tenant from organization
   */
  const handleRemoveTenant = async (tenantDbId: number) => {
    setMutationError(null);

    try {
      await removeTenant(tenantDbId);
      // React Query automatically invalidates and refetches tenants + organization
    } catch (err) {
      setMutationError(err instanceof Error ? err.message : 'Failed to remove tenant');
    }
  };

  /**
   * Handle requesting a status change for a tenant (org_owner only)
   */
  const handleRequestStatusChange = (tenant: OrganizationTenant) => {
    setStatusChangeTenant(tenant);
    setIsStatusChangeModalOpen(true);
  };

  /**
   * Handle bulk delete for federated org users
   */
  const handleFederatedBulkDelete = async () => {
    if (!organization) return;
    setIsFederatedBulkDeleting(true);
    setFederatedBulkDeleteError(null);

    try {
      const userIds = Array.from(selectedFederatedUsers);
      await OrganizationApiService.bulkDeleteUsers(organization.id, userIds);
      setSelectedFederatedUsers(new Set());
      refetchOrgMembers();
      refetchOrganization();
      setShowFederatedBulkDeleteDialog(false);
    } catch (err) {
      setFederatedBulkDeleteError(err instanceof Error ? err.message : 'Failed to delete users');
    } finally {
      setIsFederatedBulkDeleting(false);
    }
  };

  /**
   * Check if member is an invitation (not yet a user)
   */
  const isInvitation = (member: OrganizationMember): boolean => {
    return member.member_type === 'invitation';
  };

  /**
   * Check if member is a pending user (user who hasn't logged in yet)
   */
  const isPendingUser = (member: OrganizationMember): boolean => {
    return member.member_type === 'user' && member.last_login === null;
  };

  /**
   * Handle resend invite for federated org member (user or invitation)
   */
  const handleResendInvite = async (member: OrganizationMember) => {
    if (!organization) return;
    setResendingUserId(member.id);
    setMutationError(null);

    try {
      if (member.member_type === 'invitation') {
        // Resend invitation email and extend expiration
        await OrganizationApiService.resendInvitation(organization.id, member.id);
      } else {
        // Resend invite for existing user who hasn't logged in
        await OrganizationApiService.resendInvite(organization.id, member.id);
      }
      refetchOrgMembers();
    } catch (err) {
      setMutationError(err instanceof Error ? err.message : 'Failed to resend invite');
    } finally {
      setResendingUserId(null);
    }
  };

  /**
   * Handle delete member for federated org (user or invitation)
   */
  const handleDeleteOrgMember = async (member: OrganizationMember) => {
    if (!organization) return;

    // Different confirmation message for users vs invitations
    const actionText = member.member_type === 'invitation' ? 'cancel invitation for' : 'delete user';
    if (!confirm(`Are you sure you want to ${actionText} "${member.name}"?`)) {
      return;
    }

    setDeletingUserId(member.id);
    setDeletingUserName(member.name);
    setMutationError(null);
    setSuccessMessage(null);

    try {
      // Note: For invitations, we use the same endpoint which will handle the revocation
      await OrganizationApiService.removeUserFromOrganization(organization.id, member.id);
      const successText = member.member_type === 'invitation' ? 'Invitation cancelled' : 'User deleted';
      setSuccessMessage(`${successText}: "${member.name}"`);
      refetchOrgMembers();
      refetchOrganization();
    } catch (err) {
      const errorText = member.member_type === 'invitation' ? 'cancel invitation' : 'delete user';
      setMutationError(err instanceof Error ? err.message : `Failed to ${errorText}`);
    } finally {
      setDeletingUserId(null);
      setDeletingUserName('');
    }
  };

  /**
   * Find pending status change request for a tenant
   */
  const getPendingStatusChangeRequest = (tenantId: number): TenantRequest | undefined => {
    return pendingRequests?.find(
      (request) =>
        request.request_type === 'status_change' &&
        request.target_tenant_id === tenantId &&
        request.status === 'pending'
    );
  };

  /**
   * Tenant table columns - conditionally include actions in name column based on permissions
   * - MANAGE_TENANT_ACCESS: Can manage user access to tenants
   * - MANAGE_ALL_TENANTS: Can remove tenants from organization
   */
  const hasAnyTenantAction = canManageAllTenants || canManageTenantAccess;
  const tenantColumns: TableColumnDefinition<OrganizationTenant>[] = [
    hasAnyTenantAction
      ? createColumnWithRowActions<OrganizationTenant>({
          columnId: 'name',
          header: 'Tenant Name',
          compare: (a, b) => a.tenant_name.localeCompare(b.tenant_name),
          renderContent: (item) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              <Avatar
                name={item.tenant_name}
                size={32}
                color="colorful"
              />
              <Text weight="semibold" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.tenant_name}
              </Text>
            </div>
          ),
          getActions: (item) => {
            const actions: ActionConfig[] = [];
            if (canManageTenantAccess || canManageAllTenants) {
              actions.push({
                label: 'Manage User Access',
                icon: <People20Regular />,
                onClick: () => handleManageUserAccess(item),
              });
            }
            // Org owners can request status changes but not directly manage tenant status
            if (isOrgOwner && !canManageAllTenants) {
              actions.push({
                label: 'Request Status Change',
                icon: <ArrowSync20Regular />,
                onClick: () => handleRequestStatusChange(item),
              });
            }
            if (canManageAllTenants) {
              actions.push({
                label: 'Remove from Organization',
                icon: <Delete20Regular />,
                onClick: () => handleRemoveTenant(item.id),
                disabled: isRemoving,
              });
            }
            return actions;
          },
          getAriaLabel: (item) => `Actions for ${item.tenant_name}`,
        })
      : createTableColumn<OrganizationTenant>({
          columnId: 'name',
          compare: (a, b) => a.tenant_name.localeCompare(b.tenant_name),
          renderHeaderCell: () => 'Tenant Name',
          renderCell: (item) => (
            <TableCellLayout
              media={
                <Avatar
                  name={item.tenant_name}
                  size={32}
                  color="colorful"
                />
              }
            >
              <Text weight="semibold">{item.tenant_name}</Text>
            </TableCellLayout>
          ),
        }),
    createTableColumn<OrganizationTenant>({
      columnId: 'domain',
      compare: (a, b) => (a.domain || '').localeCompare(b.domain || ''),
      renderHeaderCell: () => 'Domain',
      renderCell: (item) => (
        <TableCellLayout>
          <Text>{item.domain || '-'}</Text>
        </TableCellLayout>
      ),
    }),
    createTableColumn<OrganizationTenant>({
      columnId: 'tenant_id',
      renderHeaderCell: () => 'Tenant ID',
      renderCell: (item) => (
        <TableCellLayout>
          <Text style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {item.tenant_id}
          </Text>
        </TableCellLayout>
      ),
    }),
    createTableColumn<OrganizationTenant>({
      columnId: 'status',
      compare: (a, b) => a.status.localeCompare(b.status),
      renderHeaderCell: () => 'Status',
      renderCell: (item) => {
        const pendingRequest = getPendingStatusChangeRequest(item.id);
        return (
          <TableCellLayout>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Badge
                appearance="filled"
                color={getTenantStatusColor(item.status)}
                style={{ textTransform: 'capitalize' }}
              >
                {item.status === 'managed' ? 'active' : item.status}
              </Badge>
              {pendingRequest && (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  {pendingRequest.requested_status === 'paused' ? 'pause' : 'archive'} requested
                </Text>
              )}
            </div>
          </TableCellLayout>
        );
      },
    }),
    createTextColumn<OrganizationTenant>({
      columnId: 'notes',
      headerText: 'Notes',
      getValue: (item) => item.notes || '',
      sortable: false,
    }),
  ];

  /**
   * Get display name for a pending request (handles both new_tenant and status_change types)
   */
  const getRequestDisplayName = (item: TenantRequest): string => {
    if (item.request_type === 'status_change') {
      return item.target_tenant_name || '';
    }
    return item.requested_name || '';
  };

  /**
   * Pending request table columns (read-only view for org owners)
   */
  const requestColumns: TableColumnDefinition<TenantRequest>[] = [
    createTableColumn<TenantRequest>({
      columnId: 'name',
      compare: (a, b) => getRequestDisplayName(a).localeCompare(getRequestDisplayName(b)),
      renderHeaderCell: () => 'Name',
      renderCell: (item) => {
        const displayName = getRequestDisplayName(item);
        return (
          <TableCellLayout
            media={
              <Avatar
                name={displayName}
                size={32}
                color="colorful"
              />
            }
          >
            <Text weight="semibold">{displayName}</Text>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<TenantRequest>({
      columnId: 'requestedBy',
      compare: (a, b) => a.requested_by_name.localeCompare(b.requested_by_name),
      renderHeaderCell: () => 'Requested By',
      renderCell: (item) => (
        <TableCellLayout
          media={
            <Avatar
              name={item.requested_by_name}
              size={32}
              color="colorful"
            />
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Text weight="semibold" style={{ color: tokens.colorBrandBackgroundSelected, lineHeight: '1.2' }}>
              {item.requested_by_name}
            </Text>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3, lineHeight: '1.2' }}>
              {item.requested_by_upn}
            </Text>
          </div>
        </TableCellLayout>
      ),
    }),
    createTableColumn<TenantRequest>({
      columnId: 'date',
      compare: (a, b) => {
        const dateA = new Date(a.requested_at).getTime();
        const dateB = new Date(b.requested_at).getTime();
        return dateA - dateB;
      },
      renderHeaderCell: () => 'Request Time',
      renderCell: (item) => (
        <TableCellLayout truncate>
          {new Date(item.requested_at).toLocaleString()}
        </TableCellLayout>
      ),
    }),
    createTableColumn<TenantRequest>({
      columnId: 'status',
      compare: (a, b) => a.status.localeCompare(b.status),
      renderHeaderCell: () => 'Status',
      renderCell: (item) => (
        <TableCellLayout truncate>
          {item.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
        </TableCellLayout>
      ),
    }),
    createTextColumn<TenantRequest>({
      columnId: 'notes',
      headerText: 'Notes',
      getValue: (item) => item.notes || '',
      sortable: false,
    }),
  ];

  /**
   * Get display text for a role value (matches UserTable pattern)
   */
  const getRoleDisplayText = (role: string | null): string => {
    switch (role) {
      case 'global_admin':
        return 'Global Admin';
      case 'organization_owner':
        return 'Org Owner';
      case 'organization_admin':
        return 'Org Admin';
      case 'organization_user':
        return 'Org User';
      case 'managed_tenant_user':
      default:
        return 'User';
    }
  };

  /**
   * Organization member columns (for federated orgs)
   * Shows users and pending invitations in a combined view.
   * Includes: User (avatar+name+email), Status, Role, Tenant Access
   */
  const orgMemberColumns: TableColumnDefinition<OrganizationMember>[] = [
    // User column with Avatar + Name + Email + hover actions (matches UserTable pattern)
    createColumnWithRowActions<OrganizationMember>({
      columnId: 'user',
      header: 'User',
      compare: (a, b) => a.name.localeCompare(b.name),
      renderContent: (item) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <Avatar
            name={item.name}
            size={32}
            color="colorful"
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden' }}>
            <Text weight="semibold" style={{ color: tokens.colorBrandBackgroundSelected, lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.name}
            </Text>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3, lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.email ?? '-'}
            </Text>
          </div>
        </div>
      ),
      getActions: (item) => {
        const actions: ActionConfig[] = [];

        // Only show Edit for users (not invitations)
        if (item.member_type === 'user') {
          actions.push({
            label: 'Edit User',
            icon: <Edit20Regular />,
            onClick: () => {
              // Convert OrganizationMember to OrganizationUser for editing
              const userForEdit: OrganizationUser = {
                id: item.id,
                email: item.email,
                user_principal_name: null,
                name: item.name,
                role: item.role,
                is_active: item.is_active,
                accessible_tenant_ids: item.accessible_tenants.map(t => t.tenant_id),
                accessible_tenants: item.accessible_tenants,
                last_login: item.last_login,
              };
              setEditingOrgUser(userForEdit);
              setIsOrgUserCreateModalOpen(true);
            },
          });
        }

        // Show Resend Invite for invitations and pending users
        if (isInvitation(item) || isPendingUser(item)) {
          actions.push({
            label: resendingUserId === item.id ? 'Sending...' : 'Resend Invite',
            icon: <Mail20Regular />,
            onClick: () => handleResendInvite(item),
            disabled: resendingUserId === item.id,
          });
        }

        // Delete/Cancel action - different label for invitations vs users
        const deleteLabel = item.member_type === 'invitation' ? 'Cancel Invitation' : 'Delete User';
        const deletingLabel = item.member_type === 'invitation' ? 'Cancelling...' : 'Deleting...';
        actions.push({
          label: deletingUserId === item.id ? deletingLabel : deleteLabel,
          icon: <Delete20Regular />,
          onClick: () => handleDeleteOrgMember(item),
          disabled: deletingUserId === item.id,
        });

        return actions;
      },
      getAriaLabel: (item) => `Actions for ${item.name}`,
    }),
    // Status column with badges
    createTableColumn<OrganizationMember>({
      columnId: 'status',
      compare: (a, b) => a.status.localeCompare(b.status),
      renderHeaderCell: () => 'Status',
      renderCell: (item) => {
        if (item.member_type === 'invitation') {
          return (
            <TableCellLayout>
              <Badge appearance="tint" color="warning">
                Pending Invitation
              </Badge>
            </TableCellLayout>
          );
        }
        return (
          <TableCellLayout>
            <Badge appearance="tint" color={item.is_active ? 'success' : 'subtle'}>
              {item.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </TableCellLayout>
        );
      },
    }),
    // Role column (matches UserTable pattern)
    createTableColumn<OrganizationMember>({
      columnId: 'role',
      compare: (a, b) => a.role.localeCompare(b.role),
      renderHeaderCell: () => 'Role',
      renderCell: (item) => (
        <TableCellLayout>
          <Text>{getRoleDisplayText(item.role)}</Text>
        </TableCellLayout>
      ),
    }),
    // Tenant Access column - uses accessible_tenants with names from all orgs
    createTableColumn<OrganizationMember>({
      columnId: 'tenant_access',
      renderHeaderCell: () => 'Tenant Access',
      renderCell: (item) => {
        const accessibleTenants = item.accessible_tenants ?? [];
        return (
          <TableCellLayout>
            {accessibleTenants.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {accessibleTenants.map(tenant => (
                  <Text key={tenant.tenant_id}>{tenant.tenant_name}</Text>
                ))}
              </div>
            ) : (
              <Text>-</Text>
            )}
          </TableCellLayout>
        );
      },
    }),
  ];

  /**
   * Render toolbar for Users tab
   */
  const renderUsersToolbar = () => (
    <DataGridToolbar>
      <DataGridToolbar.Left>
        <ToolbarButton
          icon={<ArrowClockwise20Regular />}
          onClick={() => refetchOrgMembers()}
          loading={isFetchingOrgMembers}
          loadingText="Refreshing..."
          data-testid="org-user-refresh"
        >
          Refresh
        </ToolbarButton>
      </DataGridToolbar.Left>
      <DataGridToolbar.Right>
        <ToolbarButton
          icon={<Delete24Regular />}
          onClick={() => setShowFederatedBulkDeleteDialog(true)}
          disabled={selectedFederatedUsers.size === 0}
          data-testid="org-user-bulk-delete"
        >
          {selectedFederatedUsers.size > 0 ? `Delete (${selectedFederatedUsers.size})` : 'Delete'}
        </ToolbarButton>
        <ToolbarButton
          appearance="primary"
          icon={<Add20Regular />}
          onClick={() => {
            setEditingOrgUser(undefined);
            setIsOrgUserCreateModalOpen(true);
          }}
          data-testid="org-user-add"
        >
          Add User
        </ToolbarButton>
      </DataGridToolbar.Right>
    </DataGridToolbar>
  );

  /**
   * Render toolbar for Tenants tab
   */
  const renderTenantsToolbar = () => (
    <DataGridToolbar>
      <DataGridToolbar.Left>
        <ToolbarButton
          icon={<ArrowClockwise20Regular />}
          onClick={handleRefresh}
          loading={isFetchingTenants}
          loadingText="Refreshing..."
          data-testid="tenants-refresh"
        >
          Refresh
        </ToolbarButton>
      </DataGridToolbar.Left>
      {canManageAllTenants && (
        <DataGridToolbar.Right>
          <ToolbarButton
            appearance="primary"
            icon={<Add20Regular />}
            onClick={handleOpenAddTenantDialog}
            data-testid="add-tenant-button"
          >
            Add Tenant
          </ToolbarButton>
        </DataGridToolbar.Right>
      )}
    </DataGridToolbar>
  );

  /**
   * Render toolbar for Pending Requests tab
   */
  const renderRequestsToolbar = () => (
    <DataGridToolbar>
      <DataGridToolbar.Left>
        <ToolbarButton
          icon={<ArrowClockwise20Regular />}
          onClick={() => refetchPendingRequests()}
          loading={isFetchingPendingRequests}
          loadingText="Refreshing..."
          data-testid="refresh-requests-button"
        >
          Refresh
        </ToolbarButton>
      </DataGridToolbar.Left>
      {user?.role === 'organization_owner' && (
        <DataGridToolbar.Right>
          <ToolbarButton
            appearance="primary"
            icon={<Add20Regular />}
            onClick={() => setIsRequestTenantDialogOpen(true)}
            data-testid="request-tenant-button"
          >
            Request Tenant
          </ToolbarButton>
        </DataGridToolbar.Right>
      )}
    </DataGridToolbar>
  );

  /**
   * Render header actions with org info (when not in direct tab access)
   */
  const renderHeaderActions = () => {
    if (isDirectUsersAccess || isDirectTenantsAccess || !organization) return null;
    return (
      <div className={styles.headerInfo}>
        <Badge
          appearance="filled"
          color={getStatusColor(organization.status)}
          className={styles.statusBadge}
        >
          {organization.status}
        </Badge>
        <div className={styles.countBadge}>
          <People20Regular />
          <Text>{organization.user_count} users</Text>
        </div>
        <div className={styles.countBadge}>
          <Organization20Regular />
          <Text>{organization.tenant_count} tenants</Text>
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <PageLayout title="Organization" testId="organization-detail-loading">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner size="large" label="Loading organization..." />
        </div>
      </PageLayout>
    );
  }

  // Error state
  if (error && !organization) {
    return (
      <PageLayout
        title="Organization"
        onBack={showBackButton ? handleBack : undefined}
        testId="organization-detail-error"
      >
        <Alert intent="error">
          <Text>{error}</Text>
        </Alert>
      </PageLayout>
    );
  }

  // Not found state
  if (!organization) {
    return (
      <PageLayout
        title="Organization"
        onBack={showBackButton ? handleBack : undefined}
        testId="organization-detail-not-found"
      >
        <Alert intent="warning">
          <Text>Organization not found</Text>
        </Alert>
      </PageLayout>
    );
  }

  // Determine the page title based on context
  const pageTitle = isDirectUsersAccess
    ? 'Users'
    : isDirectTenantsAccess
      ? 'Tenants'
      : organization.name;

  // Determine tabs config - only show when not in direct tab access mode
  const requestCount = pendingRequests?.length ?? 0;
  // Determine which tabs to show based on access method
  const tabsConfig = isDirectUsersAccess
    ? undefined // Users-only view: no tabs
    : isDirectTenantsAccess
      ? [
          // Tenants access: show Tenants and Pending Requests tabs
          { value: 'tenants', label: 'Tenants' },
          { value: 'requests', label: `Pending Requests${requestCount > 0 ? ` (${requestCount})` : ''}` },
        ]
      : [
          // Full view (global admin): show all tabs
          { value: 'users', label: 'Users' },
          { value: 'tenants', label: 'Tenants' },
          { value: 'requests', label: `Pending Requests${requestCount > 0 ? ` (${requestCount})` : ''}` },
        ];

  return (
    <PageLayout
      title={pageTitle}
      tabs={tabsConfig}
      selectedTab={selectedTab}
      onTabChange={(value) => setSelectedTab(value as DetailTab)}
      headerActions={renderHeaderActions()}
      onBack={showBackButton ? handleBack : undefined}
      testId="organization-detail"
    >
      {/* Error Alerts */}
      {error && (
        <Alert intent="error">
          <Text>{error}</Text>
        </Alert>
      )}
      {mutationError && (
        <Alert intent="error">
          <Text>{mutationError}</Text>
        </Alert>
      )}
      {successMessage && (
        <Alert intent="success">
          <Text>{successMessage}</Text>
        </Alert>
      )}
      {deletingUserId && (
        <Alert intent="info" data-testid="delete-user-loading">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Spinner size="tiny" />
            <Text>Deleting user &ldquo;{deletingUserName}&rdquo;...</Text>
          </div>
        </Alert>
      )}

      {/* Users Tab - Federated organizations (queries local database) */}
      {selectedTab === 'users' && organization && isFederatedOrg && (() => {
        const allMembers = orgMembers ?? [];
        const totalMembers = allMembers.length;
        const totalPages = Math.ceil(totalMembers / orgUsersPageSize);
        const startIndex = (orgUsersPage - 1) * orgUsersPageSize;
        const paginatedMembers = allMembers.slice(startIndex, startIndex + orgUsersPageSize);

        return (
          <DataGridCard
            items={paginatedMembers}
            columns={orgMemberColumns}
            getRowId={(item) => item.id}
            defaultSortState={{ sortColumn: 'user', sortDirection: 'ascending' }}
            columnSizingOptions={{
              user: { minWidth: 200, defaultWidth: 280 },
              status: { minWidth: 100, defaultWidth: 140 },
              role: { minWidth: 100, defaultWidth: 120 },
              tenant_access: { minWidth: 150, defaultWidth: 200 },
            }}
            toolbar={renderUsersToolbar()}
            pagination={{
              showing: paginatedMembers.length,
              total: totalMembers,
              page: orgUsersPage,
              totalPages: totalPages || 1,
              onPageChange: setOrgUsersPage,
            }}
            selectable={true}
            selectedIds={selectedFederatedUsers}
            onSelectionChange={(ids) => setSelectedFederatedUsers(ids as Set<number>)}
            emptyMessage="No members in this organization"
            testId="org-members-table"
          />
        );
      })()}

      {/* Users Tab - Home tenant organizations (uses Graph API via UserManagementPage) */}
      {selectedTab === 'users' && organization && !isFederatedOrg && (
        <UserManagementPage
          ref={userManagementRef}
          organizationId={organization.id}
          tenantId={homeTenant?.tenant_id}
          embedded={true}
          searchQuery={userSearchQuery}
          onSearchChange={setUserSearchQuery}
        />
      )}

      {/* Tenants Tab */}
      {selectedTab === 'tenants' && (() => {
        const allTenants = tenants ?? [];
        const totalTenants = allTenants.length;
        const totalPages = Math.ceil(totalTenants / tenantsPageSize);
        const startIndex = (tenantsPage - 1) * tenantsPageSize;
        const paginatedTenants = allTenants.slice(startIndex, startIndex + tenantsPageSize);

        return (
          <>
            {renderTenantsToolbar()}
            <DataGridCard
              items={paginatedTenants}
              columns={tenantColumns}
              getRowId={(item) => item.id.toString()}
              defaultSortState={{ sortColumn: 'name', sortDirection: 'ascending' }}
              columnSizingOptions={{
                name: { minWidth: 150, defaultWidth: 200 },
                domain: { minWidth: 200, defaultWidth: 280 },
                tenant_id: { minWidth: 280, defaultWidth: 320 },
                status: { minWidth: 100, defaultWidth: 120 },
              }}
              pagination={{
                showing: paginatedTenants.length,
                total: totalTenants,
                page: tenantsPage,
                totalPages: totalPages || 1,
                onPageChange: setTenantsPage,
              }}
              loading={isLoadingTenants}
              emptyMessage="No tenants assigned to this organization"
              testId="organization-tenants-table"
            />
          </>
        );
      })()}

      {/* Pending Requests Tab */}
      {selectedTab === 'requests' && (() => {
        const allRequests = pendingRequests ?? [];
        const totalRequests = allRequests.length;
        const totalPages = Math.ceil(totalRequests / requestsPageSize);
        const startIndex = (requestsPage - 1) * requestsPageSize;
        const paginatedRequests = allRequests.slice(startIndex, startIndex + requestsPageSize);

        return (
          <DataGridCard
            items={paginatedRequests}
            columns={requestColumns}
            getRowId={(item) => item.id}
            defaultSortState={{ sortColumn: 'name', sortDirection: 'ascending' }}
            columnSizingOptions={{
              requestedBy: { minWidth: 200, defaultWidth: 280 },
            }}
            toolbar={renderRequestsToolbar()}
            pagination={{
              showing: paginatedRequests.length,
              total: totalRequests,
              page: requestsPage,
              totalPages: totalPages || 1,
              onPageChange: setRequestsPage,
            }}
            loading={pendingRequestsLoading}
            emptyMessage="No pending requests"
            testId="pending-requests-table"
          />
        );
      })()}

      {/* Add Tenant Modal - allows granting tenant access to users at the same time */}
      <AddTenantModal
        open={isAddTenantDialogOpen}
        orgId={orgIdNumber ?? 0}
        unassignedTenants={unassignedTenants}
        orgUsers={(orgMembers ?? [])
          .filter((m): m is OrganizationMember => m.member_type === 'user')
          .map((m): OrganizationUser => ({
            id: m.id,
            email: m.email,
            user_principal_name: null,
            name: m.name,
            role: m.role,
            is_active: m.is_active,
            accessible_tenant_ids: m.accessible_tenants.map(t => t.tenant_id),
            accessible_tenants: m.accessible_tenants,
            last_login: m.last_login,
          }))}
        onClose={() => setIsAddTenantDialogOpen(false)}
        onSuccess={() => {
          // Refetch data to show updated tenants and user access
          refetchOrgMembers();
          // React Query will also invalidate tenants automatically
        }}
      />

      {/* Request Tenant Dialog - for organization owners to request new tenants */}
      <Dialog
        open={isRequestTenantDialogOpen}
        onOpenChange={(_event, data) => setIsRequestTenantDialogOpen(data.open)}
      >
        <DialogSurface style={{ maxWidth: '600px' }}>
          <DialogBody>
            <DialogTitle>Request New Tenant</DialogTitle>
            <DialogContent>
              <TenantRegistrationForm
                mode="request"
                organizationId={organization?.id}
                onSuccess={handleRequestTenantSuccess}
                onCancel={() => setIsRequestTenantDialogOpen(false)}
              />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Org User Create/Edit Modal */}
      <OrgUserCreateModal
        open={isOrgUserCreateModalOpen}
        orgId={organization?.id ?? 0}
        primaryDomain={organization?.primary_domain}
        onClose={() => {
          setIsOrgUserCreateModalOpen(false);
          setEditingOrgUser(undefined);
        }}
        onSuccess={() => {
          // Only refresh data - don't close modal here
          // Modal handles its own closing via onClose callback
          // (TAP info screen needs modal to stay open)
          refetchOrgMembers();
          refetchOrganization();
        }}
        user={editingOrgUser}
        organizationTenants={tenants ?? []}
        onAddTenantClick={canManageAllTenants ? handleOpenAddTenantDialog : undefined}
      />

      {/* Bulk Delete Dialog */}
      <Dialog
        open={showFederatedBulkDeleteDialog}
        onOpenChange={(_event, data) => setShowFederatedBulkDeleteDialog(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Users</DialogTitle>
            <DialogContent>
              {federatedBulkDeleteError && (
                <div style={{ marginBottom: '16px' }}>
                  <Alert intent="error">
                    <Text>{federatedBulkDeleteError}</Text>
                  </Alert>
                </div>
              )}
              <Text>
                Are you sure you want to delete {selectedFederatedUsers.size} user{selectedFederatedUsers.size !== 1 ? 's' : ''}?
                This action cannot be undone.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setShowFederatedBulkDeleteDialog(false)}
                disabled={isFederatedBulkDeleting}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleFederatedBulkDelete}
                disabled={isFederatedBulkDeleting}
              >
                {isFederatedBulkDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Tenant User Access Modal */}
      {userAccessTenant && (
        <TenantUserAccessModal
          isOpen={isUserAccessModalOpen}
          tenant={userAccessTenant}
          onClose={() => {
            setIsUserAccessModalOpen(false);
            setUserAccessTenant(null);
          }}
        />
      )}

      {/* Tenant Status Change Modal (for org_owner to request status changes) */}
      {statusChangeTenant && (
        <TenantStatusChangeModal
          isOpen={isStatusChangeModalOpen}
          tenant={statusChangeTenant}
          onClose={() => {
            setIsStatusChangeModalOpen(false);
            setStatusChangeTenant(null);
          }}
          onSuccess={() => {
            // Refresh pending requests list after successful submission
            refetchPendingRequests();
          }}
        />
      )}
    </PageLayout>
  );
};
